<script setup lang="ts">
import { ref, computed } from 'vue'
import type { KnowledgeBaseEntry } from '~/composables/useKnowledgeBase'

const props = defineProps<{
  entry: KnowledgeBaseEntry
}>()

const emit = defineEmits<{
  (e: 'save', entry: KnowledgeBaseEntry): void
  (e: 'cancel'): void
}>()

const isCreating = computed(() => !props.entry.title)
const formData = ref({
  ...props.entry,
})

const previewTab = ref(false)

// Simple markdown parser for demo purposes
function simpleMarkdown(text: string): string {
  if (!text) return ''

  // Process headers
  let html = text
    .replace(/# (.*?)$/gm, '<h1>$1</h1>')
    .replace(/## (.*?)$/gm, '<h2>$1</h2>')
    .replace(/### (.*?)$/gm, '<h3>$1</h3>')

  // Process lists
  html = html
    .replace(/- (.*?)$/gm, '<li>$1</li>')
    .replace(/(<li>.*?<\/li>)/gs, '<ul>$1</ul>')

  // Process bold and italic
  html = html
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')

  // Process paragraphs
  html = html.replace(/^([^<].*?)$/gm, '<p>$1</p>')

  // Remove empty paragraphs
  html = html.replace(/<p><\/p>/g, '')

  return html
}

const markdownPreview = computed(() => {
  return simpleMarkdown(formData.value.content || '')
})

function handleSubmit() {
  emit('save', {
    ...formData.value,
    updatedAt: new Date().toISOString(),
  })
}

function handleCancel() {
  emit('cancel')
}
</script>

<template>
  <form @submit.prevent="handleSubmit">
    <div class="space-y-6">
      <div>
        <label for="title" class="block text-sm font-medium text-gray-700"
          >Title</label
        >
        <div class="mt-1">
          <input
            id="title"
            v-model="formData.title"
            class="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
            name="title"
            placeholder="Knowledge base entry title"
            required
            type="text"
          />
        </div>
      </div>

      <div>
        <label
          for="pathPattern"
          class="block text-sm font-medium text-gray-700"
        >
          Path Pattern
        </label>
        <div class="mt-1">
          <input
            id="pathPattern"
            v-model="formData.pathPattern"
            class="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
            name="pathPattern"
            placeholder="/route-path or /route-path* for wildcard"
            required
            type="text"
          />
        </div>
        <p class="mt-1 text-sm text-gray-500">
          Add a * at the end for wildcard matching (e.g. /products* will match
          /products, /products/123, etc.)
        </p>
      </div>

      <div>
        <div class="flex justify-between items-center">
          <label for="content" class="block text-sm font-medium text-gray-700"
            >Content</label
          >
          <div class="flex space-x-2">
            <button
              :class="[
                'px-3 py-1 text-sm font-medium rounded-md',
                !previewTab
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
              ]"
              type="button"
              @click="previewTab = false"
            >
              Editor
            </button>
            <button
              :class="[
                'px-3 py-1 text-sm font-medium rounded-md',
                previewTab
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
              ]"
              type="button"
              @click="previewTab = true"
            >
              Preview
            </button>
          </div>
        </div>
        <div class="mt-1">
          <div v-if="!previewTab">
            <textarea
              id="content"
              v-model="formData.content"
              class="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
              name="content"
              placeholder="Use Markdown formatting for content"
              required
              rows="12"
            />
            <p class="mt-1 text-sm text-gray-500">
              Use Markdown for formatting. Supports headings, lists, links,
              bold, italic, etc.
            </p>
          </div>
          <div
            v-else
            class="border border-gray-300 rounded-md p-4 prose min-h-[300px] max-w-none"
          >
            <div v-if="formData.content" v-html="markdownPreview"></div>
            <div v-else class="text-gray-500 italic">
              Preview will appear here
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end space-x-3">
        <button
          class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          type="button"
          @click="handleCancel"
        >
          Cancel
        </button>
        <button
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          type="submit"
        >
          {{ isCreating ? 'Create' : 'Update' }} Knowledge Base Entry
        </button>
      </div>
    </div>
  </form>
</template>
